import React, { useState, useEffect, useCallback } from 'react';
import { Upload, Download, Undo2, FileWarning } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND } from './ImpactDashboardTab';

const BATCH_STATUS = {
  preview: { background: '#f1f5f9', color: '#64748b' },
  committed: { background: '#f0f9e8', color: '#4d7c0f' },
  reverted: { background: '#fdf2f5', color: '#be123c' },
};

export default function ImpactImports({ API_URL, getAuthHeader, refreshKey }) {
  const [types, setTypes] = useState([]);
  const [batches, setBatches] = useState([]);
  const [importType, setImportType] = useState('people');
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState('create_only');
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [tr, br] = await Promise.all([
        fetch(`${API_URL}/api/impact/imports/types`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/impact/imports`, { headers: getAuthHeader() }),
      ]);
      if (tr.ok) setTypes(await tr.json());
      if (br.ok) setBatches(await br.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader]);

  useEffect(() => { fetchAll(); }, [fetchAll, refreshKey]);

  const downloadTemplate = async () => {
    const r = await fetch(`${API_URL}/api/impact/imports/template/${importType}`, { headers: getAuthHeader() });
    if (!r.ok) return toast.error('Could not download template');
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `dcelc_${importType}_template.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append('import_type', importType);
    fd.append('file', file);
    try {
      const r = await fetch(`${API_URL}/api/impact/imports/preview`, { method: 'POST', headers: getAuthHeader(), body: fd });
      const d = await r.json();
      if (r.ok) { setPreview(d); toast.success('File validated — review the preview before committing'); }
      else toast.error(d.detail || 'Validation failed');
    } catch { toast.error('Upload failed'); }
    setBusy(false);
  };

  const commit = async () => {
    setBusy(true);
    const r = await fetch(`${API_URL}/api/impact/imports/${preview.id}/commit`, {
      method: 'POST', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode })
    });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { toast.success(`Imported: ${d.created} created, ${d.updated} updated, ${d.skipped} skipped`); setPreview(null); fetchAll(); }
    else toast.error(d.detail || 'Commit failed');
    setBusy(false);
  };

  const discardPreview = async () => {
    await fetch(`${API_URL}/api/impact/imports/${preview.id}`, { method: 'DELETE', headers: getAuthHeader() }).catch(() => {});
    setPreview(null);
    fetchAll();
  };

  const revert = async (id) => {
    if (!window.confirm('Revert this batch? Records it created will be removed. Updates to pre-existing records are preserved to protect later manual corrections.')) return;
    const r = await fetch(`${API_URL}/api/impact/imports/${id}/revert`, { method: 'POST', headers: getAuthHeader() });
    const d = await r.json().catch(() => ({}));
    if (r.ok) { toast.success(`Reverted — ${d.removed_created_records} created record(s) removed`); fetchAll(); }
    else toast.error(d.detail || 'Revert failed');
  };

  const downloadErrors = async (id) => {
    const r = await fetch(`${API_URL}/api/impact/imports/${id}/errors`, { headers: getAuthHeader() });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `import_errors.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const spec = types.find(t => t.key === importType);

  return (
    <div className="space-y-5" data-testid="impact-imports">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-bold mb-1" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>Import data (CSV)</h3>
        <p className="text-xs text-slate-400 mb-4">Validate → preview → commit. Re-importing the same file never duplicates records; no emails or certificates are ever triggered by imports.</p>
        <div className="flex flex-wrap items-center gap-3">
          <select value={importType} onChange={e => { setImportType(e.target.value); setPreview(null); }}
            data-testid="impact-import-type" className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
            {types.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
          </select>
          <button onClick={downloadTemplate} data-testid="impact-import-template-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold border border-slate-300 text-slate-600 hover:bg-slate-50">
            <Download className="w-3.5 h-3.5" /> Download template
          </button>
          <label className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white cursor-pointer ${busy ? 'opacity-60' : ''}`} style={{ background: BRAND.teal }}>
            <Upload className="w-3.5 h-3.5" /> {busy ? 'Working…' : 'Upload CSV'}
            <input type="file" accept=".csv,text/csv" className="hidden" data-testid="impact-import-file"
              onChange={e => { uploadFile(e.target.files[0]); e.target.value = ''; }} disabled={busy} />
          </label>
        </div>
        {spec && <p className="text-[11px] text-slate-400 mt-3">Columns: {spec.columns.join(', ')} · Required: {spec.required.join(', ')} · Max 2 MB / 2,000 rows</p>}
      </div>

      {preview && (
        <div className="bg-white rounded-xl border-2 p-5" style={{ borderColor: BRAND.gold }} data-testid="impact-import-preview">
          <h4 className="font-bold mb-2" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>Preview — nothing written yet</h4>
          <div className="flex flex-wrap gap-2 text-xs mb-4">
            <span className="px-3 py-1.5 rounded-lg bg-slate-100 font-semibold text-slate-600">{preview.rows_total} rows in file</span>
            <span className="px-3 py-1.5 rounded-lg font-semibold" style={{ background: '#f0f9e8', color: '#4d7c0f' }} data-testid="impact-import-new-count">{preview.new_count} new</span>
            <span className="px-3 py-1.5 rounded-lg font-semibold" style={{ background: '#e6f7f6', color: BRAND.teal }}>{preview.update_count} proposed update(s)</span>
            <span className={`px-3 py-1.5 rounded-lg font-semibold ${preview.rows_error ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-400'}`} data-testid="impact-import-error-count">{preview.rows_error} error row(s)</span>
          </div>

          {preview.error_rows?.length > 0 && (
            <div className="border border-rose-200 bg-rose-50/50 rounded-lg p-3 mb-4 text-xs" data-testid="impact-import-errors">
              <p className="font-bold text-rose-700 mb-1.5 flex items-center gap-1"><FileWarning className="w-3.5 h-3.5" /> Rows with problems (will be skipped)</p>
              {preview.error_rows.slice(0, 8).map((e, i) => (
                <p key={i} className="text-rose-600">Row {e.row}: {e.problems.join('; ')}</p>
              ))}
              {preview.error_rows.length > 8 && <p className="text-rose-500 mt-1">…and {preview.error_rows.length - 8} more. Download the error file after committing.</p>}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <select value={mode} onChange={e => setMode(e.target.value)} data-testid="impact-import-mode" className="border border-slate-300 rounded-lg px-3 py-2 text-xs bg-white">
              <option value="create_only">Create new records only (skip matches)</option>
              <option value="create_and_update">Create new + update matched records</option>
            </select>
            <button onClick={commit} disabled={busy || preview.rows_valid === 0} data-testid="impact-import-commit"
              className="px-4 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50" style={{ background: BRAND.navy }}>
              Commit {preview.rows_valid} valid row(s)
            </button>
            <button onClick={discardPreview} className="px-4 py-2 rounded-lg text-xs font-bold text-slate-500 border border-slate-200">Discard</button>
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Blank fields never erase existing values. Legacy aggregates are stored as separate summary records and never blend into unique-person totals.</p>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <h4 className="font-bold px-5 pt-4 pb-2" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>Import history</h4>
        <table className="w-full text-sm" data-testid="impact-import-history">
          <thead><tr className="text-left text-xs uppercase text-slate-500 border-b bg-slate-50">
            <th className="py-2.5 px-5">File</th><th>Type</th><th>Status</th><th>Rows</th><th>Result</th><th>Operator</th><th>Date</th><th></th>
          </tr></thead>
          <tbody>
            {batches.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400">No imports yet.</td></tr>}
            {batches.map(b => (
              <tr key={b.id} className="border-b border-slate-100" data-testid={`impact-import-batch-${b.id}`}>
                <td className="py-2.5 px-5 font-medium text-xs" style={{ color: BRAND.navy }}>{b.filename}</td>
                <td className="text-xs">{b.type}</td>
                <td><span className="text-[10px] font-black uppercase px-2 py-0.5 rounded" style={BATCH_STATUS[b.status]}>{b.status}</span></td>
                <td className="text-xs">{b.rows_valid}/{b.rows_total} valid</td>
                <td className="text-xs">{b.result ? `${b.result.created} created · ${b.result.updated} updated · ${b.result.skipped} skipped` : '—'}</td>
                <td className="text-xs text-slate-400">{b.operator}</td>
                <td className="text-xs text-slate-400">{String(b.created_at).slice(0, 10)}</td>
                <td className="px-2">
                  <span className="flex gap-1">
                    {b.rows_error > 0 && <button onClick={() => downloadErrors(b.id)} title="Download error rows" className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50"><FileWarning className="w-3.5 h-3.5" /></button>}
                    {b.status === 'committed' && <button onClick={() => revert(b.id)} data-testid={`impact-import-revert-${b.id}`} title="Revert batch" className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Undo2 className="w-3.5 h-3.5" /></button>}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
